import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FilterBarSelectInput from 'components/@common/FilterBar/FilterBarSelectInput';
import upperFirst from 'lodash/upperFirst';
import Button from '@material-ui/core/Button';
import CancelIcon from '@material-ui/icons/Cancel';
import Grid from '@material-ui/core/Grid';
import MenuItem from '@material-ui/core/MenuItem';
import { makeStyles, Theme } from '@material-ui/core/styles';
import {
  Organization,
  Maybe,
  OrganizationRoleClassification,
  UserStatus,
  ProductFamily
} from 'types/schema-generated-types';
import FilterBarSearchInput from 'components/@common/FilterBar/FilterBarSearchInput';
import omit from 'lodash/omit';
export interface UserManagementListFilter {
  userStatus?: UserStatus | '';
  organizationRoleClassification?: OrganizationRoleClassification | '';
  productFamilyCode?: string;
  organizationId?: number | '';
  search: string;
}
interface FilterBarProps {
  values: UserManagementListFilter;
  handleChange: (
    eventOrPath: string | React.ChangeEvent<any>
  ) => void | ((eventOrTextValue: string | React.ChangeEvent<any>) => void);
  resetForm: VoidFunction;
  organizations: Maybe<Array<Organization>>;
  productFamilies: Maybe<Array<ProductFamily>>;
}

const useStyles = makeStyles((theme: Theme) => ({
  reportControlsContainer: {
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    paddingTop: theme.spacing() * 1.5,
    paddingBottom: theme.spacing() * 1.5,
    boxShadow: theme.shadows[2],
    background: 'white'
  },
  select: {
    width: '100%'
  },
  buttonContainer: {
    height: '100%'
  },
  label: {
    fontWeight: 600
  },
  iconButton: {
    width: 36,
    height: 36,
    minWidth: 'unset',
    padding: 'unset'
  },
  icon: {
    height: 18,
    width: 18
  }
}));

const ORGANIZATION_ROLE_MENU_OPTIONS = Object.values(
  omit(OrganizationRoleClassification, 'AUDITOR')
).map(option => (
  <MenuItem key={option} value={option}>
    {upperFirst(option.toLowerCase())}
  </MenuItem>
));

const USER_STATUS_MENU_OPTIONS = Object.values(UserStatus).map(status => (
  <MenuItem key={`${status}-userStatus-option`} value={status}>
    {upperFirst(status.toLowerCase())}
  </MenuItem>
));

const UserManagementFilterBar: React.FC<FilterBarProps> = ({
  values,
  handleChange,
  resetForm,
  organizations,
  productFamilies
}) => {
  const classes = useStyles();
  const { t } = useTranslation('UserManagementFilterBar');

  const productFamilyMenuOptions = useMemo(
    () =>
      // eslint-disable-next-line react/display-name
      productFamilies?.map(family => (
        <MenuItem key={`${family.id}-product-option`} value={family.code}>
          {family.name}
        </MenuItem>
      )),
    [productFamilies]
  );

  const organizationMenuOptions = useMemo(
    () =>
      organizations?.length ? (
        // eslint-disable-next-line react/display-name
        organizations?.map(org => (
          <MenuItem key={`${org.type}-${org.id}-option`} value={org.id}>
            {org.name}
          </MenuItem>
        ))
      ) : (
        <MenuItem disabled>No organizations available</MenuItem>
      ),
    [organizations]
  );

  return (
    <div className={classes.reportControlsContainer}>
      <Grid container spacing={3}>
        <Grid xs={12} md={2} item>
          <FilterBarSelectInput
            inputProps={{
              'aria-label': t('Product Used')
            }}
            formControlProps={{
              className: classes.select
            }}
            onChange={handleChange}
            name="productFamilyCode"
            placeholder={t('Product Used')}
            value={values.productFamilyCode}
          >
            {productFamilyMenuOptions}
          </FilterBarSelectInput>
        </Grid>
        <Grid xs={12} md={2} item>
          <FilterBarSelectInput
            formControlProps={{
              className: classes.select
            }}
            placeholder={t('User Status')}
            value={values.userStatus}
            size="small"
            inputProps={{
              'aria-label': t('User Status')
            }}
            name="userStatus"
            onChange={handleChange}
          >
            {USER_STATUS_MENU_OPTIONS}
          </FilterBarSelectInput>
        </Grid>
        <Grid xs={12} md={2} item>
          <FilterBarSelectInput
            formControlProps={{
              className: classes.select
            }}
            placeholder={t('User Role')}
            name="organizationRoleClassification"
            value={values.organizationRoleClassification}
            size="small"
            inputProps={{
              'aria-label': t('User Role')
            }}
            onChange={handleChange}
          >
            {ORGANIZATION_ROLE_MENU_OPTIONS}
          </FilterBarSelectInput>
        </Grid>
        <Grid xs={12} md={2} item>
          <FilterBarSelectInput
            formControlProps={{
              className: classes.select
            }}
            name="organizationId"
            placeholder={t('Organizations')}
            value={values.organizationId}
            size="small"
            type="number"
            inputProps={{
              'aria-label': t('Organizations')
            }}
            onChange={handleChange}
          >
            {organizationMenuOptions}
          </FilterBarSelectInput>
        </Grid>
        <Grid xs={12} md={2} item>
          <FilterBarSearchInput
            inputProps={{
              'aria-label': t('Search')
            }}
            onChange={handleChange}
            name="search"
            placeholder={t('Search')}
            value={values.search}
          />
        </Grid>
        <Grid xs={12} md={2} item>
          <Grid container alignContent="center" className={classes.buttonContainer}>
            <Grid item>
              <Button
                variant="text"
                size="small"
                disableElevation
                onClick={resetForm}
                startIcon={<CancelIcon />}
              >
                Clear All Filters
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default UserManagementFilterBar;
